/**
 * Charge management + case-analysis wiring (DOM-facing feature layer).
 *
 * Faithful ports of the monolith's charge handlers and analyzeCase. The
 * monolith's dual-typed `state.activeCharges` is split here:
 *   - the FREE-TEXT charge list ({id,text}[]) lives in `state.chargeList` and is
 *     driven by addChargeToList / deleteCharge /
 *     renderChargesList / displayStatuteDetails;
 *   - the charge-type SLUGS live in `state.activeCharges` and are driven by
 *     initChargeGrid / toggleCharge / getDetectedCharges.
 *
 * Statute lookups use the lazily-loaded MGL singleton (`getMglSearch()`), which
 * may be null before assets load — every lookup degrades gracefully.
 */

import type { ChargeSlug } from '@/types';
import { store } from '@/app/store';
import { byId, inputValue, setInputValue } from '@/ui/dom';
import { showToast, showModal, closeModal } from '@/ui/overlays';
import { getMglSearch, loadMglSearch } from '@/app/mgl';
import { parseCitation, formatCitation, lookupStatute, getDetectedCharges, allChargeText } from '@/core/citations';
import { buildCaseAnalysisHtml } from '@/core/analysis';
import { escapeHTML } from '@/utils/formatters';
import { CHARGES } from '@/data/charges';
import { DEFAULT_ELEMENTS } from '@/data/elements';
import { getCaselawDb } from '@/data/caselaw-db-instance';
import type { CrimeHit, SubOffense, JuryResearchHit } from '@/data/caselaw-db';
import { caseLawForStatuteHtml } from '@/features/law-cite';
// renderElements + switchCaseTab live in the (not-yet-ported) case feature; see
// the port report — '@/features/case' must export both.
import { renderElements, switchCaseTab, syncCaseForm } from '@/features/case';
import { parseDocket, toCaseImport, type CaseImport } from '@/core/masscourts-parse';

const EMPTY_ELEMENTS_HTML =
  '<p style="font-size:12px;color:var(--text-secondary);">No elements generated yet. Enter a statute/charge above and tap Analyze.</p>';

// ═══════════════════════════════════════════════════════
//  FREE-TEXT CHARGE LIST (state.chargeList)
// ═══════════════════════════════════════════════════════

/**
 * Add whatever is in the charge field to the list, resolving it first if it
 * reads as a citation.
 *
 * Previously this only moved text out of a separate "charge description"
 * textarea that a lookup had filled. That textarea held nothing the list
 * didn't immediately hold too, so it duplicated its own destination and made
 * adding a charge a two-step operation. One field now does both jobs: a
 * citation is looked up and stored as "c. 265, § 13M — <heading>", and
 * anything that isn't a citation is added verbatim.
 */
export async function addChargeToList(): Promise<void> {
  // Falls back to the old textarea id so a stale cached page still works.
  const raw = (inputValue('statute-lookup-input') || inputValue('inp-charge-description')).trim();
  if (!raw) {
    showToast('Enter a citation or a charge description');
    return;
  }

  let chargeText = raw;
  let note = '✓ Charge added';
  const parsed = parseCitation(raw);
  if (parsed) {
    const mgl = await loadMglSearch();
    const statute = mgl ? lookupStatute(parsed.chapter, parsed.section, mgl.citations) : null;
    const citation = formatCitation(parsed);
    chargeText = statute ? `${citation} — ${statute.heading}` : citation;
    note = statute ? `📖 ${chargeText}` : `✓ ${citation} — not in the statute index, added as written`;
  }

  const state = store.getState();
  state.chargeList.push({ id: Date.now(), text: chargeText });
  setInputValue('statute-lookup-input', '');
  setInputValue('inp-charge-description', '');
  renderChargesList();
  store.save();
  showToast(note);
  displayStatuteDetails().catch((e) => console.error('Error displaying statute details:', e));
}

export function deleteCharge(chargeId: number): void {
  const state = store.getState();
  state.chargeList = state.chargeList.filter((c) => c.id !== chargeId);
  renderChargesList();
  store.save();
  showToast('Charge removed');
  displayStatuteDetails().catch((e) => console.error('Error displaying statute details:', e));
}

export function renderChargesList(): void {
  const list = byId('charges-list');
  if (!list) return;
  const state = store.getState();
  if (state.chargeList.length === 0) {
    list.innerHTML = '';
    const elementsList = byId('elements-list');
    if (elementsList) elementsList.innerHTML = EMPTY_ELEMENTS_HTML;
    return;
  }
  list.innerHTML = state.chargeList
    .map(
      (c) =>
        '<div style="display:flex;align-items:flex-start;gap:8px;padding:10px;background:#f8fafc;border-radius:9px;border-left:3px solid var(--blue-primary);">' +
        '<div style="flex:1;font-size:12px;color:var(--text-primary);line-height:1.5;">' +
        escapeHTML(c.text) +
        '</div>' +
        '<button class="btn btn-sm" style="background:#dc2626;color:#fff;padding:6px 10px;margin-top:0;flex-shrink:0;" onclick="deleteCharge(' +
        c.id +
        ')">' +
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14H6L5,6"/><path d="M10,11v6"/><path d="M14,11v6"/><path d="M9,6V4h6v2"/></svg>' +
        '</button>' +
        '</div>'
    )
    .join('');

  // Also display statute details in the Elements box
  displayStatuteDetails().catch((e) => console.error('Error displaying statute details:', e));
}

/** Minimal shape of a `data/mgl/chapter-*.json` file (fallback statute source). */
interface ChapterFile {
  sections?: Array<{ section: string; text?: string }>;
}

/**
 * Extract statute details from each free-text charge and render them into the
 * Elements box. Operates on the free-text `chargeList` (reads `.text`).
 */
export async function displayStatuteDetails(): Promise<void> {
  const state = store.getState();
  const elementsList = byId('elements-list');
  if (state.chargeList.length === 0) {
    if (elementsList) elementsList.innerHTML = EMPTY_ELEMENTS_HTML;
    return;
  }

  let detailsHtml = '';

  const mgl = getMglSearch();
  // Offline crimes DB (elements + penalty). Degrades gracefully if unavailable.
  const crimesDb = await getCaselawDb().catch(() => null);

  // Process each charge and fetch its data
  for (let i = 0; i < state.chargeList.length; i++) {
    const charge = state.chargeList[i];
    // Accepts BOTH the dash shorthand ("265-13M") and the prose form
    // ("G.L. c. 265, § 13M — ..."). This used to match only the dash form,
    // which meant the app could not read back what its own "Look up" button
    // had just written: that button inserts prose, so every looked-up charge
    // reported "Unable to parse statute format" and silently showed no
    // elements. parseCitation() is the same parser the lookup itself uses, so
    // the two can no longer drift apart.
    const parsed = parseCitation(charge.text);
    if (parsed) {
      const chapter = parsed.chapter;
      const section = parsed.section;
      const statute = mgl ? lookupStatute(chapter, section, mgl.citations) : null;
      let foundTitle: string | null = null;

      // If not in mglSearch, try the chapter JSON file
      if (!statute) {
        try {
          const response = await fetch('data/mgl/chapter-' + chapter + '.json');
          if (!response.ok) throw new Error('File not found: ' + response.status);

          const data = (await response.json()) as ChapterFile;

          // Find the section in the array
          let sectionData: { section: string; text?: string } | null = null;
          if (data.sections && Array.isArray(data.sections)) {
            for (let j = 0; j < data.sections.length; j++) {
              if (data.sections[j].section == section) {
                sectionData = data.sections[j];
                break;
              }
            }
          }

          if (sectionData && sectionData.text) {
            // Try multiple patterns to extract the title
            // Pattern 1: "Section X: Title" (with colon)
            let titleMatch = sectionData.text.match(/Section\s+[\dA-Z]+\s*:\s*([^\r\n]+)/i);

            // Pattern 2: "Section X<whitespace>\nTitle" (title on next line after section header)
            if (!titleMatch) {
              titleMatch = sectionData.text.match(/Section\s+[\dA-Z]+\s*[\r\n]+\s*([^\r\n]+)/i);
            }

            // Pattern 3: "Section X Title" (space-separated, be cautious about capturing "Section")
            if (!titleMatch) {
              const lines = sectionData.text.split(/[\r\n]+/);
              for (let k = 0; k < lines.length; k++) {
                const line = lines[k].trim();
                if (line.match(/^Section\s+[\dA-Z]+/i)) {
                  // Found the section line, get the title part
                  const titlePart = line.replace(/^Section\s+[\dA-Z]+\s*:?\s*/i, '').trim();
                  if (titlePart && titlePart.length > 0 && !titlePart.match(/^Search|^Print|^Prev|^Next/i)) {
                    foundTitle = titlePart;
                  }
                  break;
                }
              }
            }

            if (titleMatch && titleMatch[1]) {
              foundTitle = titleMatch[1].trim();
            }

            if (foundTitle) {
              // Remove HTML entities if present
              foundTitle = foundTitle
                .replace(/&#x27;/g, "'")
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, '&')
                .replace(/&#39;/g, "'");
            }
          }
        } catch (e) {
          // File not found or parsing error
          console.error(
            'Error loading statute ' + chapter + '-' + section + ': ' + (e instanceof Error ? e.message : String(e))
          );
        }
      } else {
        foundTitle = statute.heading;
      }

      if (foundTitle) {
        detailsHtml +=
          '<div style="padding:10px;background:#f0fdf4;border-radius:8px;border-left:3px solid #22c55e;font-size:12px;margin-bottom:8px;line-height:1.6;">';
        detailsHtml += '<strong>Chapter ' + chapter + ', Section ' + section + '</strong><br>';
        detailsHtml += '<span style="color:#059669;">' + foundTitle + '</span>';
        detailsHtml += '</div>';
      } else {
        detailsHtml +=
          '<div style="padding:10px;background:#fef3c7;border-radius:8px;border-left:3px solid #f59e0b;font-size:12px;margin-bottom:8px;line-height:1.6;">';
        detailsHtml += '<strong>Chapter ' + chapter + ', Section ' + section + '</strong><br>';
        detailsHtml += '<span style="color:#b45309;">(Title not found in database)</span>';
        detailsHtml += '</div>';
      }

      // Elements + penalty from the offline crimes database (if this section
      // defines an offense we've extracted elements for).
      if (crimesDb) {
        let crime: CrimeHit | null = null;
        try {
          crime = crimesDb.getCrime(chapter, section);
        } catch {
          crime = null; // crimes table absent on a stale OPFS copy — skip
        }
        if (crime) {
          // Connect this criminal statute to case law (offline citers + search)
          // and to the juror research mapped to this charge category.
          const caseLaw = await caseLawForStatuteHtml(chapter, section);
          const juryResearch = crimesDb ? juryResearchHtml(crimesDb.researchForCharge(chapter, section)) : '';
          detailsHtml += crimeDetailHtml(crime, caseLaw + juryResearch);
        }
      }
    }
  }

  if (!elementsList) return;
  if (detailsHtml) {
    elementsList.innerHTML = detailsHtml;
  } else {
    elementsList.innerHTML =
      '<p style="font-size:12px;color:var(--text-secondary);">Unable to parse statute format. Use format like "265-1".</p>';
  }
}

/**
 * Render the "Juror-selection research for this charge" panel: findings mapped
 * to the charge's category (homicide, OUI, drugs, ...), collapsed by default.
 * '' when the charge category has no mapped research.
 */
function juryResearchHtml(hits: Array<JuryResearchHit & { label: string }>): string {
  if (!hits.length) return '';
  const label = hits[0].label;
  const rows = hits
    .map(
      (h) =>
        `<div style="background:#fff;border:1px solid #e9d8fd;border-radius:7px;padding:7px 9px;margin-bottom:5px;">` +
        `<div style="font-size:11px;font-weight:700;color:#1e293b;">${escapeHTML(h.topic || '')}</div>` +
        (h.summary
          ? `<div style="font-size:10.5px;color:#475569;line-height:1.5;margin-top:2px;">${escapeHTML(h.summary)}</div>`
          : '') +
        (h.prosecution
          ? `<div style="font-size:10px;color:#9a3412;margin-top:3px;"><b>Prosecution:</b> ${escapeHTML(h.prosecution)}</div>`
          : '') +
        (h.defense
          ? `<div style="font-size:10px;color:#1e40af;margin-top:2px;"><b>Defense:</b> ${escapeHTML(h.defense)}</div>`
          : '') +
        '</div>'
    )
    .join('');
  return (
    `<details style="background:#faf5ff;border:1px solid #e9d8fd;border-radius:8px;padding:8px 11px;margin-top:9px;">` +
    `<summary style="font-size:10px;font-weight:700;color:#6b21a8;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;">Juror-selection research — ${escapeHTML(label)} (${hits.length})</summary>` +
    `<div style="margin-top:8px;">${rows}</div>` +
    '</details>'
  );
}

/** Colored severity pill (felony / misdemeanor / concurrent felony); '' when unspecified. */
function severityBadge(sev: string | null): string {
  const style = (bg: string, fg: string): string =>
    `display:inline-block;background:${bg};color:${fg};border-radius:4px;padding:1px 7px;` +
    `font-size:9px;font-weight:800;letter-spacing:.4px;vertical-align:middle;`;
  if (sev === 'felony') return `<span style="${style('#fee2e2', '#b91c1c')}">FELONY</span>`;
  if (sev === 'misdemeanor') return `<span style="${style('#fef3c7', '#b45309')}">MISDEMEANOR</span>`;
  // A concurrent felony carries BOTH a house-of-correction and a state-prison
  // sentence, so where it is prosecuted decides the exposure: the District
  // Court can impose at most 2.5 years in a house of correction and cannot
  // commit to state prison, while the Superior Court may do either. Which
  // court hears it is the district attorney's charging decision. Given its own
  // badge and tooltip rather than being flattened into either of the above —
  // that flattening is precisely what hides the risk from counsel.
  if (sev === 'concurrent felony') {
    return (
      `<span title="Carries both a house of correction and a state prison sentence. ` +
      `District Court can impose up to 2.5 years in a house of correction and cannot commit to state prison; ` +
      `Superior Court can impose either. The district attorney decides which court hears it." ` +
      `style="${style('#ede9fe', '#6d28d9')}">CONCURRENT FELONY</span>`
    );
  }
  // Not a crime at all. Under G.L. c.90C s.1 an automobile-law violation whose
  // maximum penalty carries no imprisonment is a civil motor vehicle
  // infraction — decided by a clerk-magistrate, never tried to a jury, and no
  // conviction results. Saying so plainly matters: these look exactly like
  // criminal charges in a citation list.
  if (sev === 'civil infraction') {
    return (
      `<span title="Civil motor vehicle infraction under G.L. c. 90C, § 1 — not a crime. ` +
      `No imprisonment is available, it is heard by a clerk-magistrate rather than a jury, ` +
      `and it produces no criminal conviction." ` +
      `style="${style('#dbeafe', '#1d4ed8')}">CIVIL INFRACTION — NOT A CRIME</span>`
    );
  }
  // Distinct from the c.90C infraction above: a regulatory or administrative
  // sanction (licensure bar, agency-imposed civil penalty) outside the motor
  // vehicle law, so it gets its own wording rather than borrowing c.90C's.
  if (sev === 'civil penalty') {
    return (
      `<span title="Civil penalty only — not a crime. This section's sole sanction is regulatory ` +
      `or administrative (for example a civil penalty or a bar from licensure); it carries no ` +
      `imprisonment, no prosecution, and no criminal conviction." ` +
      `style="${style('#dbeafe', '#1d4ed8')}">CIVIL PENALTY — NOT A CRIME</span>`
    );
  }
  return '';
}

/** Numbered list of elements. */
function elementsOl(els: string[]): string {
  if (!els.length) return '';
  return (
    '<ol style="margin:6px 0 0;padding-left:20px;">' +
    els
      .map(
        (e) =>
          `<li style="font-size:12px;color:#1e293b;line-height:1.55;margin-bottom:5px;">${escapeHTML(e)}</li>`
      )
      .join('') +
    '</ol>'
  );
}

/** Render one sub-offense (a distinct variant within a crime section). */
function subOffenseHtml(s: SubOffense): string {
  const label = s.label || s.subsection || 'Variant';
  return (
    '<div style="margin-top:8px;padding:8px 10px;background:#fff;border:1px solid #e2e8f0;border-radius:7px;">' +
    `<div style="font-size:11px;font-weight:700;color:#1e293b;">${escapeHTML(label)} ${severityBadge(s.severity)}</div>` +
    elementsOl(s.elements) +
    (s.penalty
      ? `<div style="font-size:11px;color:#475569;margin-top:5px;"><b>Penalty:</b> ${escapeHTML(s.penalty)}</div>`
      : '') +
    '</div>'
  );
}

/**
 * Render the elements + penalty card for a crime looked up from the offline
 * crimes DB. Hand-verified crimes get a green check; machine-extracted ones a
 * "verify" flag. Always reminds that the judge instructs the jury on the
 * operative elements.
 */
function crimeDetailHtml(crime: CrimeHit, caseLawHtml = ''): string {
  const provenance = crime.elementsVerified
    ? '<span style="color:#059669;font-weight:700;">✓ Hand-verified elements</span>'
    : '<span style="color:#b45309;font-weight:700;">⚠ Statute-derived — verify against the statute and current case law</span>';

  const penalty =
    crime.penalty && crime.penalty.trim()
      ? `<div style="font-size:12px;color:#334155;margin-top:8px;"><b>Penalty:</b> ${escapeHTML(crime.penalty)}` +
        (crime.penaltyCitation && crime.penaltyCitation !== crime.citation
          ? ` <span style="color:#94a3b8;">(${escapeHTML(crime.penaltyCitation)})</span>`
          : '') +
        '</div>'
      : '';

  const subs = crime.subOffenses.length
    ? '<div style="margin-top:8px;font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.4px;">Distinct offenses in this section</div>' +
      crime.subOffenses.map(subOffenseHtml).join('')
    : '';

  // Neither kind of civil matter is prosecuted, so neither carries a burden
  // "the Commonwealth must prove" in the criminal sense. The two differ in HOW
  // they're decided, though, so the footer distinguishes them: a c.90C motor
  // vehicle infraction goes to a clerk-magistrate, while a regulatory civil
  // penalty is imposed by an agency.
  const isMvInfraction = crime.severity === 'civil infraction';
  const isCivilPenalty = crime.severity === 'civil penalty';
  const isCivil = isMvInfraction || isCivilPenalty;
  const provePhrase = isCivil ? 'Elements of the violation' : 'Elements the Commonwealth must prove';
  const groupPhrase = isCivil ? 'Violations in this section' : 'Offenses in this section';

  const elementsHeader = crime.elements.length
    ? `<div style="font-size:11px;font-weight:700;color:#1e3a8a;">${provePhrase} ` +
      `${severityBadge(crime.severity)}</div>` +
      elementsOl(crime.elements)
    : crime.subOffenses.length
      ? `<div style="font-size:11px;font-weight:700;color:#1e3a8a;">${groupPhrase} ${severityBadge(crime.severity)}</div>`
      : `<div style="font-size:11px;font-weight:700;color:#1e3a8a;">${severityBadge(crime.severity)}</div>`;

  return (
    '<div style="padding:11px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;margin:-2px 0 10px;line-height:1.5;">' +
    elementsHeader +
    subs +
    penalty +
    caseLawHtml +
    `<div style="font-size:10px;color:#64748b;margin-top:9px;border-top:1px solid #dbeafe;padding-top:7px;line-height:1.5;">${provenance}<br>` +
    // There is no jury and no jury instruction for a civil motor vehicle
    // infraction, so the standard footer would be actively misleading on one.
    (isMvInfraction
      ? 'A civil motor vehicle infraction is decided by a clerk-magistrate under G.L. c. 90C — there is no jury and no criminal conviction.'
      : isCivilPenalty
        ? 'This section carries a civil penalty only — enforced administratively, with no prosecution, no jury, and no criminal conviction.'
        : 'The presiding judge instructs the jury on the operative elements for the offense charged.') +
    '</div>' +
    '</div>'
  );
}

// ═══════════════════════════════════════════════════════
//  MASSCOURTS DOCKET IMPORT
// ═══════════════════════════════════════════════════════

const JURY_LABELS: Record<string, string> = {
  'change-of-venue': 'Change of venue',
  questionnaire: 'Jury questionnaire filed',
  'individual-voir-dire': 'Individual voir dire requested',
  'juror-information': 'Access to juror information',
  'additional-challenges': 'Additional peremptory challenges'
};

/** The portal. One constant so the handoff and the help text cannot drift. */
export const MASSCOURTS_URL = 'https://www.masscourts.org/';

/**
 * Open the "Import docket" modal: a two-step handoff to MassCourts and back.
 *
 * Step 1 hands off to the portal, step 2 takes the paste. Both are on screen at
 * once rather than being separate screens, because the attorney leaves the app
 * between them — coming back to a modal that is already showing the paste box
 * is the whole point. Leaving and returning does not reset it.
 *
 * The handoff is a plain link with target=_blank, NOT an in-app webview and NOT
 * a login form. That is a deliberate security choice, and the reason the app
 * asks for no MassCourts credential anywhere:
 *
 *   - Sign-in happens on the court's own page, with the real address bar
 *     visible, so a spoofed login cannot be mistaken for the real one.
 *   - Safari/iCloud Keychain and third-party password managers can autofill
 *     there. An in-app webview has its own cookie jar and would force a fresh
 *     login every time.
 *   - SpatchVenire therefore never sees, transmits or stores the password, and
 *     there is nothing to update here when the attorney changes it.
 *
 * If a MassCourts username/password field is ever proposed for this app, this
 * comment is the objection: it would take on custody of a credential that the
 * paste-based import does not need.
 */
export function showDocketImport(): void {
  const online = navigator.onLine;

  const portal = online
    ? `<a href="${MASSCOURTS_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" ` +
      `style="width:100%;box-sizing:border-box;text-decoration:none;display:flex;align-items:center;justify-content:center;gap:6px;">` +
      `Open MassCourts ↗</a>` +
      `<p style="font-size:10px;color:var(--text-secondary);line-height:1.55;margin:7px 0 0;">` +
      `Opens the court's own site in your browser, where you sign in as usual — your saved password fills in there if you use one. ` +
      `<b>SpatchVenire never sees your MassCourts password</b> and has nowhere to store it.</p>`
    : `<div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:9px 11px;font-size:11px;color:#92400e;line-height:1.55;">` +
      `You are offline, so MassCourts cannot be opened. The import itself works with no signal — paste docket text you have already copied.</div>`;

  showModal(
    `<div style="font-weight:700;font-size:15px;color:#1e293b;margin-bottom:4px;">Import a MassCourts docket</div>` +
      `<p style="font-size:11px;color:#475569;line-height:1.6;margin:0 0 12px;">The docket <b>number</b> alone cannot fill the case in. The caption, judge, counsel and charges exist only in the docket text you copy from the portal.</p>` +

      `<div style="font-size:10px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px;">Step 1 — Get the docket text</div>` +
      portal +
      `<p style="font-size:10px;color:#475569;line-height:1.55;margin:8px 0 0;">In MassCourts: open the case, click <b>All Information</b>, then select the whole page and copy.</p>` +

      `<div style="font-size:10px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:.4px;margin:14px 0 6px;">Step 2 — Paste it here</div>` +
      `<button class="btn btn-secondary btn-sm" style="width:100%;margin-bottom:7px;" onclick="pasteDocketFromClipboard()">📋 Paste from clipboard</button>` +
      `<textarea id="docket-import-text" rows="8" placeholder="…or paste docket text here" style="width:100%;box-sizing:border-box;font-size:11px;font-family:'Courier New',monospace;"></textarea>` +
      `<p style="font-size:10px;color:var(--text-secondary);line-height:1.55;margin:6px 0 0;">Parsed on this device. Nothing is uploaded.</p>` +

      `<div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px;">` +
      `<button class="btn btn-sm" onclick="closeModal()">Cancel</button>` +
      `<button class="btn btn-primary btn-sm" onclick="importDocketFromModal()">Import</button></div>`
  );
}

/**
 * Fill the paste box from the clipboard, for the return trip from MassCourts.
 *
 * Deliberately behind a button rather than run on modal open: reading the
 * clipboard prompts on iOS, and a prompt the attorney did not ask for — over a
 * clipboard that may hold something unrelated and privileged — is not a
 * reasonable thing to spring on them. Falls back to manual paste wherever the
 * API is missing or refused, which includes any non-secure origin.
 */
export async function pasteDocketFromClipboard(): Promise<void> {
  const box = byId('docket-import-text') as HTMLTextAreaElement | null;
  if (!box) return;
  try {
    const text = await navigator.clipboard.readText();
    if (!text.trim()) {
      showToast('Clipboard is empty — copy the docket page first');
      return;
    }
    box.value = text;
    showToast('Pasted — check it looks right, then tap Import');
  } catch {
    box.focus();
    showToast('Paste manually into the box (long-press → Paste)');
  }
}

/**
 * Step-by-step help for the docket import, opened from the "?" beside the
 * Docket Number field.
 *
 * It leads with WHERE the text comes from, because that is the part that trips
 * people up: the app has no connection to MassCourts and cannot look a case up
 * from the docket number. Nothing is encoded in the number but a year, a court
 * division and a case type — the caption, judge, counsel and charges only exist
 * in the docket text the attorney copies.
 */
export function showDocketImportHelp(): void {
  const step = (n: number, html: string): string =>
    `<div style="display:flex;gap:8px;margin-bottom:7px;">` +
    `<span style="flex-shrink:0;width:17px;height:17px;border-radius:50%;background:var(--blue-primary);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;">${n}</span>` +
    `<span style="font-size:11px;color:#1e293b;line-height:1.6;">${html}</span></div>`;
  const head = (t: string): string =>
    `<div style="font-size:10px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:.4px;margin:12px 0 6px;">${t}</div>`;

  showModal(
    `<div style="font-weight:700;font-size:15px;color:#1e293b;margin-bottom:4px;">Importing a docket from MassCourts</div>` +
      `<p style="font-size:11px;color:#475569;line-height:1.6;margin:0;">The docket <b>number</b> on its own cannot fill the case in — it encodes only a year, court and case type. The details live in the docket <b>text</b>, which you copy across.</p>` +
      head('Start here') +
      step(1, 'Tap <b>📥 Auto-fill from MassCourts docket</b>, then <b>Open MassCourts ↗</b>. That hands off to the court\'s own site in your browser, where you sign in as usual — this app never sees your password.') +
      head('In MassCourts') +
      step(2, 'Sign in and open your case.') +
      step(3, 'Click the <b>All Information</b> tab. It shows the header, Party, Charge and Docket sections on one page — all four are read, so a partial copy gives a partial fill.') +
      step(4, 'Select the whole page and copy it. Stray menus and navigation text are ignored.') +
      head('Back here') +
      step(5, 'The import box is still open. Tap <b>📋 Paste from clipboard</b> — or paste by hand — then <b>Import</b>.') +
      head('What it fills') +
      `<p style="font-size:11px;color:#1e293b;line-height:1.6;margin:0;">Case caption, docket number, court, county, presiding judge, the ADA and defence counsel with their BBO numbers, and the charges — all editable afterwards. Counts already nolle prossed or dismissed are counted but kept out of your working charge list. The next court date is captured too and appears in generated documents, though it has no box on this screen.</p>` +
      head('Worth knowing') +
      `<p style="font-size:11px;color:#475569;line-height:1.6;margin:0;">Parsing happens on this device — nothing is sent anywhere, and it works with no signal. Importing only writes fields it finds, so it will not blank anything you typed; to correct a bad value, clear that field first. An import also saves the case, so use <b>Delete Selected</b> under Recent Cases if you were only testing.</p>` +
      `<div style="display:flex;justify-content:flex-end;margin-top:14px;"><button class="btn btn-primary btn-sm" onclick="closeModal()">Got it</button></div>`
  );
}

/** Read the modal textarea, import, and show a result summary. */
export function importDocketFromModal(): void {
  const ta = byId<HTMLTextAreaElement>('docket-import-text');
  const text = ta ? ta.value : '';
  if (!text.trim()) {
    showToast('Paste the docket text first');
    return;
  }
  const imp = importDocketText(text);
  if (!imp.caseName && imp.chargeTexts.length === 0) {
    showToast('Could not parse a docket from that text');
    return;
  }
  closeModal();
  showDocketImportResult(imp);
}

/** Parse pasted docket text, fill Case state + charges, and return the mapping. */
export function importDocketText(text: string): CaseImport {
  const imp = toCaseImport(parseDocket(text), text);
  const state = store.getState();
  if (imp.caseName) state.caseName = imp.caseName;
  if (imp.county) state.county = imp.county;
  // The court field is a coarse <select> (e.g. "Norfolk Superior"); map the
  // parsed county+level to an existing option, else fall back to "Other MA".
  if (imp.court) {
    const courtSel = byId<HTMLSelectElement>('inp-court');
    const level = /Superior/i.test(imp.court) ? 'Superior' : 'District';
    const candidate = imp.county ? `${imp.county} ${level}` : imp.court;
    const hasOption = courtSel && Array.from(courtSel.options).some((o) => o.value === candidate);
    state.court = hasOption ? candidate : 'Other MA';
  }
  if (imp.docket) state.docket = imp.docket;
  if (imp.judge) state.judge = imp.judge;
  if (imp.trialDate) state.trialDate = imp.trialDate;
  if (imp.opp) state.opp = imp.opp;
  if (imp.prossBBO) state.prossBBO = imp.prossBBO;
  if (imp.defenseAtty) state.defenseAtty = imp.defenseAtty;
  if (imp.defBBO) state.defBBO = imp.defBBO;

  // The docket names the defendant, so the Parties tab should not open empty
  // and make you retype it. Only the NAME is taken: everything else on a
  // DefendantRecord (race, gender, SES, appearance) is counsel's own
  // observation for jury-selection purposes and is not on a docket. An existing
  // record keeps its id, timestamps and those observations — re-importing a
  // corrected docket must not wipe work already done here.
  if (imp.defendantName) {
    const now = new Date().toISOString();
    const prev = state.defendant;
    state.defendant = prev
      ? { ...prev, label: imp.defendantName, updatedAt: now }
      : {
          id: 'p' + Math.random().toString(36).slice(2, 10),
          label: imp.defendantName,
          raceEthnicity: '',
          gender: '',
          religion: '',
          appearanceNotes: '',
          socioeconomicStatus: '',
          notes: '',
          status: '',
          createdAt: now,
          updatedAt: now
        };
  }
  // REPLACE the charge list, do not merge into it.
  //
  // This used to push each docket charge onto whatever was already in
  // state.chargeList. Because chargeList survives newCase() and is not stored
  // per saved case, "whatever was already there" could be another case's
  // charges entirely — importing the Clancy docket produced
  //
  //   94C-32A POSSESSION WITH INTENT TO DISTRIBUTE CLASS B   <- a stale leftover
  //   265-1 MURDER                                           <- the actual docket
  //
  // and scored the case as murder AND drugs. A charge list that shows an
  // offence the client was never charged with is the worst failure this app
  // can have: it feeds analysis, voir dire and generated filings. The docket is
  // the authoritative statement of what the defendant is charged with, so an
  // import states the charges rather than adding to them.
  const dropped = state.chargeList
    .filter((c) => !imp.chargeTexts.some((t) => t.toLowerCase() === c.text.toLowerCase()))
    .map((c) => c.text);
  state.chargeList = imp.chargeTexts.map((t, i) => ({ id: Date.now() + i, text: t }));

  // Slugs derive from the docket charges ALONE. Deriving them from the merged
  // list is how 'drug' got switched on above.
  //
  //  - `activeCharges` (the charge-type slugs) is what SCORING reads, and
  //    chargesFor() falls back to DEFAULT_CHARGES = ['murder'] when it is empty.
  //    Every imported case was therefore scored as a murder case until someone
  //    happened to tap Analyze — right for this docket by luck, wrong for a drug
  //    or OUI case, and silent either way.
  //
  // Deliberately NOT written to `chargeDescription`: that is a dead field. Its
  // input (#inp-charge-description) no longer exists in the markup — the Charges
  // tab has a transient "Add a charge" box instead — so nothing renders it, and
  // saveCase() reads the missing element and writes '' over it on every save.
  // The charge LIST is the record; the slugs derive from it.
  state.activeCharges = getDetectedCharges(imp.chargeTexts.join('\n'));
  imp.droppedCharges = dropped;

  store.save();
  syncCaseForm();
  renderChargesList();
  return imp;
}

/** Summary modal after an import: what was filled + the jury-selection posture. */
function showDocketImportResult(imp: CaseImport): void {
  const js = imp.jurySelection
    .map((s) => {
      const label = JURY_LABELS[s.category] || s.category;
      const ruling = s.ruling ? ` — <b>${escapeHTML(s.ruling)}</b>` : '';
      const date = s.date ? ` <span style="color:#94a3b8;">(${escapeHTML(s.date)})</span>` : '';
      return `<li style="font-size:11px;color:#1e293b;margin-bottom:4px;">${escapeHTML(label)}${ruling}${date}</li>`;
    })
    .join('');
  showModal(
    `<div style="font-weight:700;font-size:15px;color:#1e293b;margin-bottom:8px;">✓ Docket imported</div>` +
      `<div style="font-size:12px;color:#334155;line-height:1.7;">` +
      (imp.caseName ? `<div><b>Case:</b> ${escapeHTML(imp.caseName)}</div>` : '') +
      (imp.docket ? `<div><b>Docket:</b> ${escapeHTML(imp.docket)}</div>` : '') +
      (imp.court ? `<div><b>Court:</b> ${escapeHTML(imp.court)}</div>` : '') +
      `<div><b>Charges set:</b> ${imp.liveChargeCount}` +
      (imp.disposedChargeCount
        ? ` <span style="color:#94a3b8;">(${imp.disposedChargeCount} disposed, skipped)</span>`
        : '') +
      `</div></div>` +
      // Replacing the list is right, but doing it silently is not: say what was
      // taken off, so a charge removed in error is visible rather than inferred
      // from an empty tab later.
      (imp.droppedCharges && imp.droppedCharges.length
        ? `<div style="margin-top:10px;background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:9px 11px;">` +
          `<div style="font-size:11px;font-weight:700;color:#92400e;margin-bottom:4px;">Removed — not on this docket</div>` +
          `<ul style="margin:0;padding-left:16px;">` +
          imp.droppedCharges
            .map(
              (t) =>
                `<li style="font-size:11px;color:#92400e;line-height:1.5;">${escapeHTML(t)}</li>`
            )
            .join('') +
          `</ul>` +
          `<div style="font-size:10px;color:#92400e;margin-top:5px;line-height:1.5;">The Charges tab now lists this docket's charges only. Re-add by hand if one belongs.</div></div>`
        : '') +
      (js
        ? `<div style="margin-top:12px;font-size:10px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:.4px;">Jury-selection posture detected</div>` +
          `<ul style="margin:6px 0 0;padding-left:18px;">${js}</ul>`
        : '') +
      `<div style="display:flex;justify-content:flex-end;margin-top:14px;"><button class="btn btn-primary btn-sm" onclick="closeModal()">Done</button></div>`
  );
}

// ═══════════════════════════════════════════════════════
//  CHARGE-SLUG GRID (state.activeCharges)
// ═══════════════════════════════════════════════════════

export function initChargeGrid(): void {
  const g = byId('charge-grid');
  if (!g) return;
  const state = store.getState();
  g.innerHTML = Object.entries(CHARGES)
    .map(([k, c]) => {
      return (
        '<div class="charge-chip' +
        (state.activeCharges.indexOf(k as ChargeSlug) !== -1 ? ' active' : '') +
        '" onclick="toggleCharge(\'' +
        k +
        '\',this)"><span>' +
        c.icon +
        '</span>' +
        c.label +
        '</div>'
      );
    })
    .join('');
}

export function toggleCharge(key: ChargeSlug, el: HTMLElement): void {
  const state = store.getState();
  const idx = state.activeCharges.indexOf(key);
  if (idx === -1) state.activeCharges.push(key);
  else state.activeCharges.splice(idx, 1);
  el.classList.toggle('active', state.activeCharges.indexOf(key) !== -1);
  store.save();
}

// ═══════════════════════════════════════════════════════
//  STATUTE LOOKUP / INSERTION
// ═══════════════════════════════════════════════════════

/** Explain the shorthand citation format in a modal (the "?" help button). */
export function showStatuteFormatHelp(): void {
  const row = (shorthand: string, means: string): string =>
    `<tr>` +
    `<td style="padding:6px 10px;border-bottom:1px solid #e2e8f0;font-family:'Courier New',monospace;font-weight:700;color:#1d4ed8;white-space:nowrap;">${shorthand}</td>` +
    `<td style="padding:6px 10px;border-bottom:1px solid #e2e8f0;color:#1e293b;">${means}</td>` +
    `</tr>`;
  showModal(
    `<div style="font-weight:700;font-size:15px;color:#1e293b;margin-bottom:4px;">Entering a statute — shorthand format</div>` +
      `<p style="font-size:12px;color:#475569;line-height:1.6;margin:0 0 10px;">` +
      `Type the chapter, then a dash, then the section — no “c.”, “§”, spaces, or commas. ` +
      `Add more dashes for each subsection or paragraph, in order.</p>` +
      `<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:9px 11px;margin-bottom:12px;font-size:12px;color:#1e3a5f;line-height:1.7;">` +
      `<b>Pattern:</b> <span style="font-family:'Courier New',monospace;">chapter-section-sub-sub-…</span><br>` +
      `Put any letter right on the number it belongs to — <span style="font-family:'Courier New',monospace;">90b</span> = chapter 90B, ` +
      `<span style="font-family:'Courier New',monospace;">26a</span> = section 26A. Case doesn’t matter.</div>` +
      `<table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;">` +
      `<tr><th style="text-align:left;padding:6px 10px;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#64748b;">You type</th>` +
      `<th style="text-align:left;padding:6px 10px;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#64748b;">Means</th></tr>` +
      row('90-24-1-a-1', 'c. 90, § 24(1)(a)(1)') +
      row('90b-26a', 'c. 90B, § 26A') +
      row('265-1', 'c. 265, § 1') +
      row('265-13a', 'c. 265, § 13A') +
      row('94c-32', 'c. 94C, § 32') +
      `</table>` +
      `<p style="font-size:11px;color:#94a3b8;line-height:1.6;margin:0;">` +
      `The full legal form still works too — e.g. <span style="font-family:'Courier New',monospace;">G.L. c. 90, § 24(1)(a)(1)</span>.</p>` +
      `<div style="display:flex;justify-content:flex-end;margin-top:14px;">` +
      `<button class="btn btn-primary btn-sm" onclick="closeModal()">Got it</button></div>`
  );
}

export async function viewStatuteFullText(chapter: string, section: string): Promise<void> {
  const mgl = getMglSearch();
  if (!mgl) return;

  try {
    const fullSection = await mgl.getSection(chapter, section);
    if (!fullSection) {
      alert('Could not load section details');
      return;
    }

    const textPreview = fullSection.text ? fullSection.text.substring(0, 500) : 'No text available';

    const content = `
      <div style="max-height:70vh;overflow-y:auto;">
        <div class="modal-title">${fullSection.citation}</div>
        <div class="detail-section">
          <div class="dl">${fullSection.heading}</div>
        </div>
        <div style="margin-top:12px;padding:12px;background:#f8fafc;border-radius:8px;border-left:3px solid var(--blue-primary);font-size:11px;line-height:1.6;color:var(--text-secondary);">
          ${textPreview}${fullSection.text && fullSection.text.length > 500 ? '...' : ''}
        </div>
        <div style="margin-top:14px;">
          <a href="${fullSection.url}" target="_blank" style="display:inline-block;padding:10px 16px;background:var(--blue-primary);color:#fff;border-radius:8px;font-size:12px;font-weight:600;text-decoration:none;">
            📖 View on malegislature.gov
          </a>
        </div>
      </div>
    `;

    const el = byId('modal-content');
    if (el) el.innerHTML = content;
  } catch (error) {
    console.error('Error loading statute text:', error);
    alert('Error loading statute. Please try again.');
  }
}

// ═══════════════════════════════════════════════════════
//  ANALYZE — Jury Profile Generator (offline, charge-based)
// ═══════════════════════════════════════════════════════

export function analyzeCase(): void {
  const state = store.getState();

  // ── Pull all inputs ───────────────────────────────────────────────────────
  state.chargeDescription = inputValue('inp-charge-description');
  state.indictmentFacts = inputValue('inp-indictment-facts');
  state.caseName = inputValue('inp-case-name');
  state.court = inputValue('inp-court');

  const detectedCharges = getDetectedCharges(allChargeText(state.chargeList, state.chargeDescription));
  state.activeCharges = detectedCharges.slice();
  const statute = state.chargeDescription || '';
  const facts = state.indictmentFacts || '';

  // chargeList counts as having a charge. Requiring `chargeDescription` meant an
  // imported case — charges listed, elements rendered — still refused to analyse
  // with "Enter a charge or facts first", because the input backing that field
  // was removed from the markup and nothing can fill it any more.
  if (!statute && !facts && !state.chargeList.length) {
    showToast('Add a charge or enter facts first');
    return;
  }

  // ── Build the analysis report (all report content lives in @/core/analysis) ─
  const content = buildCaseAnalysisHtml(store.getState());

  const analysisContent = byId('analysis-content');
  if (analysisContent) analysisContent.innerHTML = content;
  const analysisCard = byId('analysis-card');
  if (analysisCard) analysisCard.style.display = 'block';
  // TODO: applyCitationLinks (wired later)

  // ── Auto-generate elements of crimes ───────────────────────────────────────
  let autoEls: string[] = [];
  detectedCharges.forEach((k) => {
    const defs = DEFAULT_ELEMENTS[k];
    if (defs)
      defs.forEach((e) => {
        if (autoEls.indexOf(e) === -1) autoEls.push(e);
      });
  });
  if (autoEls.length === 0) {
    autoEls = [
      'The Commonwealth must prove each element beyond a reasonable doubt',
      'The defendant is presumed innocent',
      'The specific elements will be defined by the presiding judge based on the statute charged'
    ];
  }
  state.crimeElements = autoEls;
  renderElements();
  store.save();
  switchCaseTab('charges');
  if (analysisCard) analysisCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
  showToast('Jury intelligence analysis complete');
}
